import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { gsap } from '@/lib/gsap'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

/**
 * Full-bleed statement. The photograph is scrubbed directly against scroll
 * progress, so its scale and drift are tied to where the section sits in the
 * viewport rather than played back on a fixed timeline.
 */
export const Mission: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            const picture = element.querySelector<HTMLElement>('[data-mission-picture]')

            if (!picture) {
                return
            }

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.fromTo(
                picture,
                { scale: 1.26, yPercent: -6 },
                {
                    scale: 1,
                    yPercent: 6,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: element,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true
                    }
                }
            )
        }, element)

        return () => context.revert()
    }, [])

    return (
        <section
            ref={root}
            id='mission'
            className='relative min-h-[46rem] overflow-hidden bg-[color:var(--ink)] py-20 md:min-h-[54rem] md:py-28'
            aria-labelledby='mission-heading'
        >
            <div data-mission-picture className='absolute inset-0 will-change-transform'>
                <Image
                    src='/images/cap-mission.jpg'
                    alt=''
                    fill
                    sizes='100vw'
                    className='object-cover'
                />
            </div>

            <div
                className='absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/55'
                aria-hidden='true'
            />

            <div className='edge relative flex min-h-[inherit] flex-col justify-between gap-16'>
                <div className='mx-auto w-full max-w-[110rem]'>
                    <SplitText
                        as='h2'
                        lines={['Capturing moments', 'that tell your story', 'and bring emotions', 'to life']}
                        className='display-xl max-w-[18ch] text-white'
                    />

                    <span id='mission-heading' className='sr-only'>
                        The studio’s intent
                    </span>
                </div>

                <div className='mx-auto flex w-full max-w-[110rem] justify-end'>
                    <Reveal
                        delay={0.1}
                        className='w-full max-w-sm rounded-[var(--radius-lg)] border border-white/15 bg-black/45 p-7 backdrop-blur-md'
                    >
                        <p className='text-[0.875rem] leading-relaxed text-white/80'>
                            The goal is to photograph the real, unarranged emotion behind each
                            frame, so that a fleeting afternoon turns into something you can
                            still hold twenty years from now.
                        </p>

                        <Link
                            href='/contact'
                            className='pill mt-7 bg-white text-[color:var(--ink)] hover:bg-white/85'
                        >
                            Book your session
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
